import React, { useEffect, useMemo, useState } from "react"
import dayjs from "dayjs"
import { Box, Grid, Paper, Typography } from "@mui/material"
import SideBarLayout from "src/v2/layout/side-bar-layout"
import DatePickerSelectV2 from "src/v2/components/Date/date-picker-select"
import DataGridTable from "src/v2/components/Table/data-grid-table"
import ButtonWithIcon from "src/v2/components/Button/Button"
import { ReactComponent as FileIcon } from "src/v2/assets/icons/file-icon.svg"
import { dispatchSnackbar } from "src/v2/components/SnackBar/snackbar-bus"
import CurveService from "src/services/curve-service"
import { DATE_FORMAT, DATE_FORMAT_SHOW } from "src/utils/constants"
import { formatStringToFloatDecimalCase } from "src/utils/format-decimal"

const TITLE = "Tabela de Curvas"
const LAST_DATE_ERROR_MESSAGE = "Não foi possível carregar a última data disponível para Curvas."
const TABLE_ERROR_MESSAGE = "Não foi possível carregar a tabela de Curvas."
const INVALID_DATE_MESSAGE = "Informe uma data válida para consultar as Curvas."
const DATE_CONFIRMATION_MESSAGE = "Pressione Enter para atualizar a tabela."

const formatNumber = (value, minimum, maximum) =>
    value == null ? "" : formatStringToFloatDecimalCase(value, minimum, maximum)

const getNumberSortValue = (value) => {
    const numericValue = Number(value)
    return Number.isFinite(numericValue) ? numericValue : value
}

const CURVE_COLUMNS = [
    { field: "Descricao", headerName: "Descrição", flex: 1, minWidth: 250 },
    { field: "Rating", headerName: "Rating", flex: 1, minWidth: 120 },
    {
        field: "DataFormatada",
        headerName: "Data Referência",
        flex: 1,
        minWidth: 150,
        valueGetter: ({ row }) => row.DataFormatada,
    },
    {
        field: "DU",
        headerName: "DU",
        flex: 1,
        minWidth: 100,
        valueGetter: ({ row }) => formatNumber(row.DU),
        sortValueGetter: ({ value }) => getNumberSortValue(value),
    },
    {
        field: "TIR",
        headerName: "TIR",
        flex: 1,
        minWidth: 100,
        valueGetter: ({ row }) => formatNumber(row.TIR, 3, 3),
        sortValueGetter: ({ value }) => getNumberSortValue(value),
    },
    {
        field: "VolatilidadeTIR",
        headerName: "Vol TIR",
        flex: 1,
        minWidth: 110,
        valueGetter: ({ row }) => formatNumber(row.VolatilidadeTIR, 4, 4),
        sortValueGetter: ({ value }) => getNumberSortValue(value),
    },
    {
        field: "Spread",
        headerName: "Spread",
        flex: 1,
        minWidth: 120,
        valueGetter: ({ row }) => formatNumber(row.Spread, 3, 3),
        sortValueGetter: ({ value }) => getNumberSortValue(value),
    },
    {
        field: "VolatilidadeSpread",
        headerName: "Vol Spread",
        flex: 1,
        minWidth: 120,
        valueGetter: ({ row }) => formatNumber(row.VolatilidadeSpread, 4, 4),
        sortValueGetter: ({ value }) => getNumberSortValue(value),
    },
    {
        field: "DTS",
        headerName: "DTS",
        flex: 1,
        minWidth: 100,
        valueGetter: ({ row }) => formatNumber(row.DTS),
        sortValueGetter: ({ value }) => getNumberSortValue(value),
    },
]

const CurvasPage = () => {
    const curveService = useMemo(() => new CurveService(), [])
    const [loading, setLoading] = useState(false)
    const [dateSelected, setDateSelected] = useState("")
    const [lastAvailableDate, setLastAvailableDate] = useState("")
    const [lastDateStatus, setLastDateStatus] = useState("loading")
    const [lastDateRetryVersion, setLastDateRetryVersion] = useState(0)
    const [rows, setRows] = useState([])
    const [tableStatus, setTableStatus] = useState("loading")
    const [tableRetryVersion, setTableRetryVersion] = useState(0)
    const [dateError, setDateError] = useState("")
    const [datePendingConfirmation, setDatePendingConfirmation] = useState(false)

    useEffect(() => {
        let ignoreResponse = false

        const loadLastDate = async () => {
            setLoading(true)
            setLastDateStatus("loading")

            try {
                const response = await curveService.getCurvesDTS()
                const lastDate = response?.body?.data_referencia
                const parsedDate = dayjs(lastDate)

                if (ignoreResponse) return

                if (!lastDate || !parsedDate.isValid()) {
                    setDateSelected("")
                    setLastAvailableDate("")
                    setLastDateStatus("empty")
                    return
                }

                const formattedDate = parsedDate.format(DATE_FORMAT)
                setDateSelected(formattedDate)
                setLastAvailableDate(formattedDate)
                setDateError("")
                setDatePendingConfirmation(false)
                setLastDateStatus("success")
            } catch {
                if (ignoreResponse) return

                setDateSelected("")
                setLastAvailableDate("")
                setLastDateStatus("error")
                dispatchSnackbar(LAST_DATE_ERROR_MESSAGE, "error")
            } finally {
                if (!ignoreResponse) setLoading(false)
            }
        }

        loadLastDate()
        return () => {
            ignoreResponse = true
        }
    }, [curveService, lastDateRetryVersion])

    useEffect(() => {
        if (!dateSelected) return

        let ignoreResponse = false

        const loadCurves = async () => {
            setLoading(true)
            setTableStatus("loading")

            try {
                const response = await curveService.getCurves(dateSelected)
                const data = response?.body?.data ?? []
                const formattedDate = dayjs(dateSelected).format(DATE_FORMAT_SHOW)
                const curveRows = data.map((curve, index) => ({
                    ...curve,
                    id: curve.IDCurva ?? `curva-${index}`,
                    DataFormatada: formattedDate,
                }))

                if (ignoreResponse) return

                setRows(curveRows)
                setTableStatus(curveRows.length ? "success" : "empty")
            } catch {
                if (ignoreResponse) return

                setRows([])
                setTableStatus("error")
                dispatchSnackbar(TABLE_ERROR_MESSAGE, "error")
            } finally {
                if (!ignoreResponse) setLoading(false)
            }
        }

        loadCurves()
        return () => {
            ignoreResponse = true
        }
    }, [curveService, dateSelected, tableRetryVersion])

    const getDateError = (value) => {
        const parsedDate = dayjs(value)

        if (!value || !parsedDate.isValid()) return INVALID_DATE_MESSAGE

        if (lastAvailableDate && parsedDate.isAfter(dayjs(lastAvailableDate), "day")) {
            return `A última data disponível para Curvas é ${dayjs(lastAvailableDate).format(DATE_FORMAT_SHOW)}. Selecione uma data igual ou anterior.`
        }

        return ""
    }

    const handleDateDraftChange = (value) => {
        const error = getDateError(value)
        const isCurrentDate = !error && dateSelected && dayjs(value).isSame(dayjs(dateSelected), "day")

        setDateError(error)
        setDatePendingConfirmation(!error && !isCurrentDate)
    }

    const changeDate = (value) => {
        const error = getDateError(value)

        if (error) {
            setDateError(error)
            setDatePendingConfirmation(false)
            return
        }

        setDateError("")
        setDatePendingConfirmation(false)
        setDateSelected(dayjs(value).format(DATE_FORMAT))
    }

    const exportFile = async () => {
        if (!dateSelected || dateError || datePendingConfirmation || loading) return

        setLoading(true)
        try {
            await curveService.exportDataToCsv(
                dateSelected,
                dayjs(dateSelected).format(DATE_FORMAT_SHOW)
            )
        } catch {
            dispatchSnackbar("Não foi possível exportar os dados de Curvas.", "error")
        } finally {
            setLoading(false)
        }
    }

    const renderContent = () => {
        if (lastDateStatus === "error") {
            return (
                <Box role="alert" aria-live="assertive" sx={feedbackStyles}>
                    <Typography variant="textBody" sx={{ color: "error.light" }}>
                        {LAST_DATE_ERROR_MESSAGE}
                    </Typography>
                    <ButtonWithIcon size="md" onClick={() => setLastDateRetryVersion((value) => value + 1)}>
                        Tentar novamente
                    </ButtonWithIcon>
                </Box>
            )
        }

        if (lastDateStatus === "empty") {
            return <Box sx={feedbackStyles}><Typography variant="textBody">Não há uma data de referência disponível para Curvas.</Typography></Box>
        }

        if (tableStatus === "error") {
            return (
                <Box role="alert" aria-live="assertive" sx={feedbackStyles}>
                    <Typography variant="textBody" sx={{ color: "error.light" }}>{TABLE_ERROR_MESSAGE}</Typography>
                    <ButtonWithIcon size="md" onClick={() => setTableRetryVersion((value) => value + 1)}>
                        Tentar novamente
                    </ButtonWithIcon>
                </Box>
            )
        }

        if (!dateSelected) return null

        return (
            <DataGridTable
                columns={CURVE_COLUMNS}
                rows={rows}
                loading={tableStatus === "loading"}
                pageSize={25}
                resetKey={dateSelected}
                contained
                autoRowHeight
                rowPadding={2}
                disableRowSelectionOnClick
                sortingOrder={["asc", "desc"]}
                manualClientDataOperations
            />
        )
    }

    return (
        <SideBarLayout title={TITLE} header variant="v1" loading={loading} disablePageScroll headerImage="/background/eventos_headers.jpg" headerPosition="1% 42%" headerTitle={TITLE}>
            <Box sx={{ mt: -2, height: "calc(100dvh - 202px)", maxHeight: "calc(100dvh - 202px)", minHeight: 0, pb: "8px", boxSizing: "border-box", display: "flex", flexDirection: "column", overflow: "hidden", gap: 2 }}>
                <Grid container sx={{ flexShrink: 0 }}>
                    <Grid item xs={12} xl={10}>
                        <Paper sx={{ p: 3 }}>
                            <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                                <Grid item xs={12} sm={6} md={3}>
                                    {lastDateStatus === "success" && (
                                        <DatePickerSelectV2 value={dateSelected} label="Data de Exibição" onChange={changeDate} onDraftChange={handleDateDraftChange} commitOnAccept maxDate={lastAvailableDate} error={Boolean(dateError)} helperText={dateError || (datePendingConfirmation ? DATE_CONFIRMATION_MESSAGE : undefined)} />
                                    )}
                                </Grid>
                                <Grid item>
                                    <ButtonWithIcon size="md" icon={<FileIcon />} onClick={exportFile} disabled={!dateSelected || Boolean(dateError) || datePendingConfirmation || loading}>
                                        Exportar CSV
                                    </ButtonWithIcon>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>

                <Paper sx={{ flex: 1, p: 3, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
                    {renderContent()}
                </Paper>
            </Box>
        </SideBarLayout>
    )
}

const feedbackStyles = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    textAlign: "center",
}

export default CurvasPage
